import DOMPurify from "dompurify";

/**
 * Blog article HTML, made safe to hand to `dangerouslySetInnerHTML`.
 *
 * Posts are HTML by design (MAR-002) and are written by a language model, so
 * their body is text we did not author, rendered on the same origin that keeps
 * the user's tokens. A `<script>` or an `onerror` in one of them would run as
 * the app. The server sanitises on save too (backend/src/utils/sanitizeArticleHtml.ts,
 * which carries the same allowlist); this is the second layer, and the only one
 * that covers posts stored before that change.
 *
 * The allowlist is what an article needs and nothing more: no script, style,
 * iframe, object, embed or form, no `style` or event-handler attributes, and
 * only http(s), mailto, tel and relative URLs.
 */

const ALLOWED_TAGS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "dl",
  "dt",
  "dd",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "sub",
  "sup",
  "small",
  "mark",
  "abbr",
  "cite",
  "q",
  "blockquote",
  "code",
  "pre",
  "kbd",
  "a",
  "img",
  "figure",
  "figcaption",
  "table",
  "caption",
  "colgroup",
  "col",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "div",
  "span",
  "section",
  "details",
  "summary",
];

const ALLOWED_ATTR = [
  "href",
  "title",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "loading",
  "colspan",
  "rowspan",
  "scope",
  "id",
  "class",
];

// Absolute http(s), mailto and tel, or a URL with no scheme at all. Anything
// else — javascript:, data:, vbscript: — is dropped with its attribute.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

// A dedicated instance, because hooks are registered per instance and jspdf
// uses the default one for its own purposes.
const purifier = typeof window !== "undefined" ? DOMPurify(window) : null;

if (purifier) {
  // A link that opens a new tab hands that tab `window.opener` unless told not
  // to, which lets the linked site navigate this one (reverse tabnabbing).
  purifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
      node.setAttribute("rel", "noopener noreferrer");
    } else if (node.tagName === "A" && node.hasAttribute("target")) {
      // Only _blank has a use in an article; a named target could aim at a
      // frame or window the app itself opened.
      node.removeAttribute("target");
    }
  });
}

export function sanitizeArticleHtml(html: string | null | undefined): string {
  if (!html) return "";
  // Without a DOM there is nothing to render into either, so returning nothing
  // is safe and returning the input would not be.
  if (!purifier) return "";
  return purifier.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}
